import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Bell, ClipboardList, FileText, AlertTriangle, CheckCircle2, Plus, Minus, Archive, Heart, AlarmClock, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface NotificationActor {
  id: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

interface NotificationData {
  id: string;
  category: string;
  type: string;
  title: string;
  message: string | null;
  linkUrl: string;
  referenceId: string | null;
  referenceLabel: string | null;
  actorId: string | null;
  actor: NotificationActor | null;
  isRead: boolean;
  createdAt: string;
}

interface NotificationsResponse {
  notifications: NotificationData[];
  totalCount: number;
}

interface UnreadCountResponse {
  count: number;
}

type CategoryFilter = "all" | "task" | "animal_follow" | "reminder";

const CATEGORY_LABELS: Record<CategoryFilter, string> = {
  all: "All",
  task: "Tasks",
  animal_follow: "Following",
  reminder: "Reminders",
};

function getCategoryIcon(category: string) {
  switch (category) {
    case "task":
      return <ClipboardList className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
    case "report":
      return <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
    case "behavior_alert":
      return <AlertTriangle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
    case "animal_follow":
      return <Heart className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
    case "reminder":
      return <AlarmClock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
    default:
      return <Bell className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
  }
}

function getActorDisplayName(actor: NotificationActor | null): string | null {
  if (!actor) return null;
  if (actor.firstName && actor.lastName) return `${actor.firstName} ${actor.lastName}`;
  if (actor.firstName) return actor.firstName;
  return actor.username;
}

interface NotificationGroup {
  key: string;
  referenceId: string | null;
  category: string;
  items: NotificationData[];
}

function groupNotifications(notifications: NotificationData[]): NotificationGroup[] {
  const groupMap = new Map<string, NotificationGroup>();
  const orderKeys: string[] = [];

  for (const item of notifications) {
    const key = item.referenceId && item.category
      ? `${item.category}:${item.referenceId}`
      : `single:${item.id}`;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        key,
        referenceId: item.referenceId,
        category: item.category,
        items: [],
      });
      orderKeys.push(key);
    }
    groupMap.get(key)!.items.push(item);
  }

  return orderKeys.map((k) => groupMap.get(k)!);
}

function NotificationItem({
  notification,
  onNavigate,
}: {
  notification: NotificationData;
  onNavigate: (n: NotificationData) => void;
}) {
  const actorName = getActorDisplayName(notification.actor);

  return (
    <button
      className={`w-full text-left flex items-start gap-3 p-3 transition-colors hover-elevate rounded-md ${
        !notification.isRead ? "bg-accent/40" : ""
      }`}
      onClick={() => onNavigate(notification)}
      data-testid={`notification-item-${notification.id}`}
    >
      {getCategoryIcon(notification.category)}
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-tight ${!notification.isRead ? "font-medium" : "text-muted-foreground"}`}>
          {notification.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {actorName && (
            <span className="text-xs text-muted-foreground truncate">{actorName}</span>
          )}
          <span className="text-xs text-muted-foreground/70">
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          </span>
        </div>
      </div>
      {!notification.isRead && (
        <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
      )}
    </button>
  );
}

function NotificationGroupRow({
  group,
  onNavigate,
}: {
  group: NotificationGroup;
  onNavigate: (n: NotificationData) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const latest = group.items[0];
  const remainingCount = group.items.length - 1;

  if (group.items.length === 1) {
    return <NotificationItem notification={latest} onNavigate={onNavigate} />;
  }

  return (
    <div data-testid={`notification-group-${group.referenceId}`}>
      <button
        className={`w-full text-left flex items-center gap-3 p-3 transition-colors hover-elevate rounded-md ${
          group.items.some((i) => !i.isRead) ? "bg-accent/40" : ""
        }`}
        onClick={() => setExpanded(!expanded)}
        data-testid={`button-expand-group-${group.referenceId}`}
      >
        {expanded ? (
          <Minus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        ) : (
          <Plus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        )}
        {getCategoryIcon(latest.category)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm leading-tight flex-1 ${group.items.some((i) => !i.isRead) ? "font-medium" : "text-muted-foreground"}`}>
              {latest.referenceLabel
                ? `${latest.referenceLabel} has multiple notifications`
                : latest.title}
            </p>
            <Badge variant="secondary" className="text-xs flex-shrink-0 no-default-active-elevate">
              {group.items.length}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground/70">
            {formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true })}
          </span>
        </div>
        {group.items.some((i) => !i.isRead) && (
          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="ml-6 border-l pl-2">
          {group.items.map((item) => (
            <NotificationItem key={item.id} notification={item} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const categoryParam = activeCategory === "all" ? "" : `&category=${activeCategory}`;

  const { data: unreadData } = useQuery<UnreadCountResponse>({
    queryKey: ["/api/notifications/unread-count"],
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 10000,
  });

  const { data: notificationsData, isLoading } = useQuery<NotificationsResponse>({
    queryKey: [`/api/notifications?limit=50${categoryParam}`],
    enabled: open,
    staleTime: 0,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/notifications?"),
      });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => {
      const catParam = activeCategory === "all" ? "" : `?category=${activeCategory}`;
      return apiRequest("PATCH", `/api/notifications/mark-all-read${catParam}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/notifications?"),
      });
    },
  });

  const archiveReadMutation = useMutation({
    mutationFn: () => {
      const catParam = activeCategory === "all" ? "" : `?category=${activeCategory}`;
      return apiRequest("PATCH", `/api/notifications/archive-read${catParam}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/notifications?"),
      });
    },
    onError: () => {
      toast({ title: "Failed to archive notifications", variant: "destructive" });
    },
  });

  const handleNavigate = useCallback(
    (notification: NotificationData) => {
      if (!notification.isRead) {
        markReadMutation.mutate(notification.id);
      }
      setOpen(false);

      const linkUrl = notification.linkUrl;
      const commentHashMatch = linkUrl.match(/^\/tasks\/([^#]+)#comments$/);

      if (commentHashMatch && window.location.pathname === "/tasks") {
        const taskId = commentHashMatch[1];
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set("taskId", taskId);
        const newUrl = `/tasks?${currentParams.toString()}#comments`;
        window.history.replaceState(null, "", newUrl);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }

      const followTabMatch = linkUrl.match(/^\/\?tab=following&animal=(.+)$/);
      if (followTabMatch && window.location.pathname === "/") {
        window.history.pushState(null, "", linkUrl);
        window.dispatchEvent(new CustomEvent("follow-tab-navigate", {
          detail: { animal: followTabMatch[1] },
        }));
        return;
      }

      const reminderMatch = linkUrl.match(/^\/dashboard\?tab=reminders&highlight=reminder-(.+)$/);
      if (reminderMatch && window.location.pathname === "/dashboard") {
        window.history.replaceState(null, "", linkUrl);
        window.dispatchEvent(new CustomEvent("reminder-tab-navigate", {
          detail: { reminderId: reminderMatch[1] },
        }));
        return;
      }

      navigate(linkUrl);
    },
    [markReadMutation, navigate],
  );

  const unreadCount = unreadData?.count ?? 0;
  const notificationsList = notificationsData?.notifications ?? [];
  const groups = groupNotifications(notificationsList);
  const hasReadNotifications = notificationsList.some((n) => n.isRead);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/notifications?"),
      });
    }
    setOpen(isOpen);
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notifications"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 sm:w-96 p-0"
        align="end"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between gap-2 p-3 border-b">
          <h3 className="text-sm font-semibold">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Mark All Read</span>
              </Button>
            )}
            {hasReadNotifications && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => archiveReadMutation.mutate()}
                disabled={archiveReadMutation.isPending}
                data-testid="button-archive-read"
              >
                <Archive className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Archive Read</span>
              </Button>
            )}
          </div>
        </div>

        <div className="p-2 border-b">
          <div className="sm:hidden">
            <Select value={activeCategory} onValueChange={(v) => setActiveCategory(v as CategoryFilter)}>
              <SelectTrigger className="h-8 text-xs" data-testid="select-notifications-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(CATEGORY_LABELS) as [CategoryFilter, string][]).map(([key, label]) => (
                  <SelectItem key={key} value={key} data-testid={`option-notifications-${key}`}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden sm:flex items-center gap-1">
            {(Object.entries(CATEGORY_LABELS) as [CategoryFilter, string][]).map(([key, label]) => (
              <Button
                key={key}
                variant="ghost"
                size="sm"
                className={`text-xs toggle-elevate ${activeCategory === key ? "toggle-elevated" : ""}`}
                onClick={() => setActiveCategory(key)}
                data-testid={`tab-notifications-${key}`}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        <ScrollArea className="h-[min(384px,55vh)]">
          <div className="p-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : groups.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-8 gap-2"
                data-testid="notification-empty"
              >
                <CheckCircle2 className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">You're all caught up</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {groups.map((group) => (
                  <NotificationGroupRow
                    key={group.key}
                    group={group}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
